import FormHeader from "@/components/FormHeader"
import Modal from "@/components/Modal"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import api from "@/lib/axios"
import { getMaintenanceStatus } from "@/services/getData"
import { AxiosError } from "axios"
import { Loader2 } from "lucide-react"
import { useState } from "react"
import toast from "react-hot-toast"
import { IoConstructSharp } from "react-icons/io5"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

type InputState = {
  status: boolean
  hours: string | null
}

const setStatusMaintenance = async (data: InputState) => {
  const response = await api.patch("/setting/maintenance/set", data)
  return response.data
}

export default function SettingMaintenancePage() {
  const [showModal, setShowModal] = useState(false)
  const [form, setForm] = useState<InputState>({
    status: false,
    hours: null,
  })

  const { data, isPending } = useQuery({
    queryKey: ["maintenanceStatus"],
    queryFn: getMaintenanceStatus,
  })

  const queryClient = useQueryClient()
  const { mutate, isPending: isPendingSetStatus } = useMutation({
    mutationFn: setStatusMaintenance,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["maintenanceStatus"] })
      setShowModal(false)
      toast.success(data?.message || "Success.")
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{ message?: string }>
      toast.error(
        err?.response?.data?.message || "An unexpected error occurred."
      )
    },
  })

  const handleSetStatusMaintenance = (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault()

    const hours = form?.hours?.replace(" jam", "") || null
    mutate({ ...form, hours })
  }

  if (isPending) return "Loading..."

  return (
    <main className="h-[80vh] flex justify-center items-center">
      <section>
        {data && data?.system_maintenance ? (
          <p>System dalam mode maintenance</p>
        ) : (
          <p>System dalam mode operational</p>
        )}
        <br />
        <div className="w-fit mx-auto">
          <Button
            variant={"outline"}
            onClick={() => {
              setShowModal((prev) => !prev)
              setForm((prev) => ({
                ...prev,
                status: data?.system_maintenance,
              }))
            }}
          >
            Maintenance System
          </Button>
        </div>
      </section>
      <Modal show={showModal} onClose={() => setShowModal(false)}>
        <form
          onSubmit={handleSetStatusMaintenance}
          className="flex flex-col gap-y-3"
        >
          <FormHeader
            title={"Maintenance System"}
            description={"Setting status maintenance system"}
            icon={<IoConstructSharp />}
          />

          <div className="flex gap-3">
            <Switch
              id="status"
              checked={form.status}
              onCheckedChange={() =>
                setForm((prev) => ({
                  ...prev,
                  status: !prev.status,
                }))
              }
            />
            <Label htmlFor="status">
              Status Maintenance (
              <span className={!form.status ? "text-primary" : ""}>
                nonaktif
              </span>
              /<span className={form.status ? "text-primary" : ""}>aktif</span>)
            </Label>
          </div>

          <div>
            <Label>Durasi Maintenance (Jam/Hours)</Label>
            <Input
              placeholder="Ketik durasi (contoh: 24)"
              value={form.hours || ""}
              onChange={(event) => {
                const inputValue = event.target.value.replace(/\D/g, "")
                setForm((prev) => ({
                  ...prev,
                  hours: inputValue ? `${inputValue} jam` : "",
                }))
              }}
            />
          </div>

          <div className="mt-3 flex gap-3">
            <Button
              type="button"
              onClick={() => setShowModal(false)}
              disabled={isPendingSetStatus}
            >
              Batal
            </Button>
            <Button variant={"outline"} disabled={isPendingSetStatus}>
              {isPendingSetStatus ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  Processing...
                </>
              ) : (
                "Simpan"
              )}
            </Button>
          </div>
        </form>
      </Modal>
    </main>
  )
}
